// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@adl/App/Store/Reducers";
import { color, ellipsis, hideElement, mergeWithCss, xIsAuthenticated, xScreen } from "@adl/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable/Clickable";
import Icon, { IconPresentationProps, IconProps, IconWrapper } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, MouseEventHandler, useState } from "react";
import { css } from "styled-components";

interface TitleProps {
    text?: TypographyProps;
    icon?: IconProps;
}
interface HeaderClickableProps {
    onClick?: MouseEventHandler<any>;
    iconProps?: IconProps;
    titleProps?: TitleProps;
    isPopupShown?: boolean;
    numberOfCartItems?: number;
    extendedStyles?: HeaderClickableStyle;
}

type Props = HeaderClickableProps & ClickableProps;

export interface HeaderClickableStyle {
    clickable?: ClickablePresentationProps;
    icon?: IconPresentationProps;
    title?: TypographyProps;
    badgeIcon?: InjectableCss;
    cartItem?: {
        wrapper?: InjectableCss;
        text?: TypographyProps;
    };
    titleWrapper?: InjectableCss;
    titleIcon?: IconPresentationProps;
}

const headerClickableStyles: HeaderClickableStyle = {
    clickable: {
        css: css`
            align-items: flex-start;
            padding: 5px 10px;
            border-radius: 2px;
            margin-left: 0px;
            position: relative;

            &.popup-shown:not(.mobile-mode) {
                background: ${color("common.ui2")};
                transition: background 1s;
                ${IconWrapper}, & ${TypographyStyle} {
                    color: ${color("common.white")};
                }
                .cart-badge-icon {
                    background-color: ${color("common.white")};
                    color: ${color("primary")};
                }
            }

            &.mobile-mode {
                padding: 0;
                margin: 0;
                min-width: 44px;
                height: 44px;
                justify-content: center;
                align-items: center;
                &:hover,
                &.popup-shown {
                    background: ${color("common.ui2")};
                    transition: background 1s;
                    border-radius: 4px;
                    ${IconWrapper}, & ${TypographyStyle}, div.cart-mobile-mode {
                        color: ${color("common.white")};
                        border-color: ${color("common.white")};
                    }
                }
                > span,
                > span > svg {
                    width: 28px;
                    height: 28px;
                }
            }

            &:focus {
                outline: none;
            }

            &:hover {
                background: ${color("common.ui1")};
                ${IconWrapper}, & ${TypographyStyle} {
                    color: ${color("common.white")};
                }
                .cart-badge-icon {
                    background-color: ${color("common.white")};
                    color: ${color("primary")};
                }
            }
        `,
    },
    icon: {
        size: 28,
        color: "common.ui7",
    },
    title: {
        size: 12,
        weight: "bold",
        color: "primary",
        css: css`
            letter-spacing: 0.5px;
            line-height: 16px;

            display: flex;
            flex-direction: column;
            max-width: 135px;
            margin-left: 12px;
            & > .sub-title {
                letter-spacing: 0;
                font-weight: normal;
                text-transform: capitalize;
                ${ellipsis(1)}
                display: block;
                &:not(.primary) {
                    color: ${color("common.ui7")};
                }
            }

            ${xScreen("lte", "1300")} {
                ${hideElement}
            }
        `,
        ellipsis: true,
        transform: "uppercase",
    },
    cartItem: {
        wrapper: {
            css: css`
                height: 24px;
                min-width: 24px;
                border: 1px solid ${color("primary")};
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
            `,
        },
        text: {
            size: 14,
            weight: "bold",
            color: "primary",
        },
    },
    badgeIcon: {
        css: css`
            height: 20px;
            width: 20px;
            border: 2px solid ${color("common.white")};
            background-color: ${color("primary")};
            position: absolute;
            color: ${color("common.white")};
            font-size: 8px;
            font-weight: bold;
            letter-spacing: 0;
            line-height: 12px;
            text-align: center;
            left: 22px;
            top: 2px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            ${xScreen("lte", "md")} {
                ${IconWrapper} {
                    ${hideElement}
                }
            }
        `,
    },
    titleIcon: {
        css: css`
            ${xScreen("lte", "md")} {
                display: none;
            }
        `,
    },
};

const HeaderClickable: FC<Props> = ({
    numberOfCartItems,
    isPopupShown,
    iconProps,
    titleProps,
    children,
    extendedStyles,
    ...otherProps
}) => {
    const [styles] = useState(() => mergeWithCss(headerClickableStyles, extendedStyles));

    const { isTabletScreen, isAuthenticated } = useAppSelector(state => ({
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
        isAuthenticated: xIsAuthenticated(state),
    }));

    const classNames = [isPopupShown ? "popup-shown" : "", isTabletScreen ? "mobile-mode" : ""].join(" ").trim();
    const isCartMobileMode = !!(isTabletScreen && numberOfCartItems && numberOfCartItems > 0);
    return (
        <Clickable {...styles.clickable} {...otherProps} className={classNames}>
            <Icon
                {...styles.icon}
                {...iconProps}
                size={isTabletScreen ? 24 : 28}
                {...{ count: numberOfCartItems || 0 }}
            />
            {typeof numberOfCartItems === "number" && isAuthenticated && (
                <StyledWrapper {...styles.badgeIcon} className="cart-badge-icon">
                    {numberOfCartItems > 99 ? `99+` : numberOfCartItems}
                </StyledWrapper>
            )}
            {/* {isCartMobileMode && (
                <StyledWrapper className="cart-mobile-mode" {...styles.cartItem?.wrapper}>
                    <Typography {...styles.cartItem?.text} {...titleProps?.text}>
                        {numberOfCartItems}
                    </Typography>
                </StyledWrapper>
            )} */}
            <StyledWrapper {...styles.titleWrapper}>
                <Typography {...styles.title} {...titleProps?.text}>
                    {children}
                </Typography>
                {titleProps?.icon && <Icon {...styles.titleIcon} size={14} color="common.ui15" {...titleProps?.icon} />}
            </StyledWrapper>
        </Clickable>
    );
};

export default HeaderClickable;
